import { Showable } from "../../../mixins/Showable";
import { ContentsScrollPane } from "../ContentsScrollPane";
import { ItemLocChoiceData, StatisticsCategoryCompoundData, StatisticsCategoryParsedLocData, StatisticsCategoryParsedThemeData, StatisticsItemLayoutData, StatisticsLayoutData } from "../../../types/types";
import { AppLocalization, LanguageId } from "../../localization/AppLocalization";
import { StatisticsCategoryContent } from "./StatisticsCategoryContent";
import { StatisticsDataManager } from "../../managers/StatisticsDataManager";
import { LayoutDataManager, OptionsItemKeys } from "../../managers/LayoutDataManager";
import { ThemeDataManager } from "../../managers/ThemeDataManager";
import { VariationId } from "../TableScreen";


export interface StatisticsContentsScroll extends Showable { }


export class StatisticsContentsScroll
    extends ContentsScrollPane {
    // parsed data (runtime data), usable by component
    protected contentsData: StatisticsCategoryCompoundData[];
    // following 2 vars reflect current component state
    public curVariationId: VariationId;
    public curLanguageId: LanguageId;


    public override Init(
        cullItems: boolean = true,
        referenceWidth: number = undefined,
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // > referenceWidth
        // > scrollAxis
        // > excessScrollAmount
        // > contentsScrollData
        // > contents
        super.Init(cullItems, referenceWidth);

        this.excessScrollAmount.Set(0, 0, 0, 50);

        // set data
        this.curVariationId = variationId;
        this.curLanguageId = languageId;

        // update data model for Build() categories
        this.UpdateContentsData();
    }

    public Build(): void {
        // >> build overlay
        // > build content container
        // > build mask
        super.Build();

        this.contentsData.forEach((contentData: StatisticsCategoryCompoundData, i: number) => {
            // console.log(contentData);
            let catContent = new StatisticsCategoryContent(this.scene);
            // catContent.name = "OptionsCategoryContent_"+i;
            catContent.Init(contentData, i);
            catContent.Build();
            this.content.add(catContent);
            this.contents[i] = catContent;
        });
    }

    public SetData(
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // console.log("SetData");

        if (variationId != undefined) {
            this.curVariationId = variationId;
        }
        if (languageId != undefined) {
            this.curLanguageId = languageId;
        }

        // parse new models into usable runtime model
        this.UpdateContentsData();

        // NOTE: number of cats STAYS. Number/type of items within each cat CHANGES.
        this.contents.forEach((
            catContent: StatisticsCategoryContent,
            catIndex: number,
        ) => {
            catContent.SetData(this.contentsData[catIndex]);
        });
    }

    /**
     * Parse various 'current' models to usable runtime data model for categories.
     */
    private UpdateContentsData(): void {
        // console.log("UpdateContentsData");
        // console.log(VariationId[this.curVariationId], LanguageId[this.curLanguageId]);

        // fetch data
        this.contentsData = [];
        const statsCatsLiveData = StatisticsDataManager.GetVariationData(this.curVariationId).difficulties;
        const aiLevelChoices: ItemLocChoiceData[] = AppLocalization.GetSharedData(this.curLanguageId).options.itemsById[OptionsItemKeys.aiLevel].choices;
        const statsSharedLocData = AppLocalization.GetSharedData(this.curLanguageId).statistics;
        const statsLayoutData: StatisticsLayoutData = LayoutDataManager.GetVariationData(this.curVariationId).statistics;
        // const statsSharedLayoutData: StatisticsSharedLayoutData = LayoutDataManager.GetSharedData().statistics;
        const statsSharedThemeData = ThemeDataManager.GetSharedData().statistics;
        statsCatsLiveData.forEach((difficulty, difficultyIndex: number) => {
            // parse theme data
            const catThemeData: StatisticsCategoryParsedThemeData = {
                title: statsSharedThemeData.categoryItems[difficultyIndex],
            };
            // parse loc data
            const catLocData: StatisticsCategoryParsedLocData = {
                title: aiLevelChoices[difficultyIndex].itemLabel as string,
                itemsById: statsSharedLocData.itemsById,
            }
            const contentData: StatisticsCategoryCompoundData = {
                variationId: this.curVariationId,
                layout: statsLayoutData,
                // layoutShared: statsSharedLayoutData, // each cat has the same layout data
                theme: catThemeData,
                loc: catLocData,
                // live: statsCatsLiveData[categoryIndex],
            };
            this.contentsData.push(contentData);
        });
    }

    /**
     * Update scroll data for scrollPane (followed by view update: capitalizedTitle..)
     * 
     * NOTE: Called from parent through this base @see ScrollPane::UpdateFromLocalizationChange
     * NOTE: Called via parent::SetData
     * NOTE: this.contentsScrollData updated by now @see SetData
     */
    public override UpdateFromLocalizationChange(): void {
        // console.log("UpdateFromLocalizationChange");

        this.contentsData.forEach((categoryCompoundData: StatisticsCategoryCompoundData, categoryIndex: number) => {
            const catTitle: string = categoryCompoundData.loc.title;
            this.contentsScrollData[categoryIndex] = {
                title: catTitle,
                capitalizedTitle: catTitle.toUpperCase(),
            };
        });
    }
}